<template>
  <div
    class="role-canvas min-h-screen flex items-center justify-center px-6 py-12 font-sans antialiased"
  >
    <!-- Decorative watermark icons (same technique as the dashboard cards) -->
    <i class="wm wm-1 material-icons-round">local_taxi</i>
    <i class="wm wm-2 material-icons-round">navigation</i>
    <i class="wm wm-3 material-icons-round">directions_car</i>

    <!-- Loading state -->
    <div v-if="loading" class="relative flex flex-col items-center text-center">
      <div class="brand-logo flex items-center justify-center">
        <img
          src="@/assets/logo.png"
          alt="Taxiye"
          class="h-16 w-16 rounded-full"
        />
      </div>
      <div class="spinner mt-8"></div>
      <p class="mt-6 text-sm font-medium text-gray-500">Signing you in…</p>
    </div>

    <!-- Role selection -->
    <div v-else class="relative w-full max-w-3xl">
      <div class="flex flex-col items-center text-center">
        <div class="brand-logo flex items-center justify-center">
          <img
            src="@/assets/logo.png"
            alt="Taxiye"
            class="h-14 w-14 rounded-full"
          />
        </div>
        <h1 class="mt-5 text-2xl font-bold text-gray-900">Select a role</h1>
        <p class="mt-1 text-sm text-gray-400">
          Choose how you'd like to continue.
        </p>
      </div>

      <div class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <button
          v-for="role in activatedCorporateRoles"
          :key="'corp-' + role.roleName"
          type="button"
          class="role-card group flex flex-col items-center justify-center p-6 text-center bg-white rounded-2xl"
          @click="selectCorporateRole(role)"
        >
          <span
            class="role-icon flex items-center justify-center w-12 h-12 rounded-xl"
          >
            <i class="text-2xl material-icons-round">domain</i>
          </span>
          <p class="mt-4 font-semibold text-gray-900">{{ role.roleName }}</p>
          <p class="mt-1 text-xs text-gray-400">Corporate</p>
        </button>

        <button
          v-for="role in activatedRoles"
          :key="'role-' + role.roleName"
          type="button"
          class="role-card group flex flex-col items-center justify-center p-6 text-center bg-white rounded-2xl"
          @click="selectRole(role)"
        >
          <span
            class="role-icon flex items-center justify-center w-12 h-12 rounded-xl"
          >
            <i class="text-2xl material-icons-round">badge</i>
          </span>
          <p class="mt-4 font-semibold text-gray-900">{{ role.roleName }}</p>
          <p class="mt-1 text-xs text-gray-400">Admin</p>
        </button>
      </div>

      <div class="mt-10 text-center">
        <button
          type="button"
          class="inline-flex items-center text-sm font-medium text-gray-400 transition-colors duration-150 hover:text-taxiye-600"
          @click="logout()"
        >
          <i class="mr-1 text-base material-icons-round">logout</i>
          Log out
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// import { ROLES } from "@/config";

export default {
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    activatedRoles() {
      return this.$store.getters.isLoggedIn
        ? this.$store.state.auth.account.roles
        : [];
    },
    activatedCorporateRoles() {
      return this.$store.getters.isLoggedIn
        ? this.$store.state.auth.account.corporateRoles
        : [];
    },
  },
  mounted() {
    const roles = this.activatedRoles || [];
    const corporateRoles = this.activatedCorporateRoles || [];
    const total = roles.length + corporateRoles.length;

    // Single role → auto-select and keep the loading screen until we redirect,
    // so the selection UI never flashes.
    if (
      this.$store.getters.isLoggedIn &&
      roles.length === 1 &&
      corporateRoles.length === 0
    ) {
      window.flash({
        type: "info",
        body: `Using the only available role: ${roles[0].roleName}`,
      });
      this.selectRole(roles[0]);
      return;
    }

    if (total === 0) {
      // Nothing to select — send the user back to login.
      this.logout();
      return;
    }

    // Multiple roles → let the user choose.
    this.loading = false;
  },
  methods: {
    // roleName(id) {
    //   return ROLES.resolveName(id);
    // },
    async selectRole(role) {
      this.loading = true;
      await this.$store.dispatch("setRole", role);
      this.$router.push("/dashboard");
    },
    async selectCorporateRole(role) {
      this.loading = true;
      await this.$store.dispatch("setCorporateRole", role);
      this.$router.push("/corporate/dashboard");
    },
    logout() {
      this.$store.dispatch("logout").finally(() => this.$router.push("/login"));
    },
  },
};
</script>

<style scoped>
/* ── Canvas — warm branded backdrop (matches the login screen) ── */
.role-canvas {
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      120% 90% at 100% 0%,
      rgba(238, 75, 43, 0.1) 0%,
      transparent 45%
    ),
    radial-gradient(
      100% 90% at 0% 100%,
      rgba(255, 215, 0, 0.14) 0%,
      transparent 50%
    ),
    linear-gradient(180deg, #fffbf2 0%, #f4f3ef 100%);
}

/* Oversized faded watermark icons — same effect as the dashboard cards */
.wm {
  position: absolute;
  color: #ee4b2b;
  opacity: 0.06;
  pointer-events: none;
  user-select: none;
  line-height: 1;
}
.wm-1 {
  top: 8%;
  left: 8%;
  font-size: 9rem;
  transform: rotate(-15deg);
}
.wm-2 {
  top: 16%;
  right: 10%;
  font-size: 7rem;
  color: #ffd700;
  opacity: 0.16;
  transform: rotate(20deg);
}
.wm-3 {
  bottom: 10%;
  left: 12%;
  font-size: 8rem;
  transform: rotate(10deg);
}

/* Logo with the gold ring used across the app */
.brand-logo {
  border-radius: 9999px;
  box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.55),
    0 6px 16px rgba(77, 50, 0, 0.18);
}

/* Loading spinner — gold ring on a taxiye-orange head */
.spinner {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  border: 3px solid rgba(255, 215, 0, 0.3);
  border-top-color: #ee4b2b;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Role cards ─────────────────────────────────────────────── */
.role-card {
  border: 1px solid #f0e6cc;
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.04),
    0 12px 24px -8px rgba(77, 50, 0, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
}
.role-card:hover {
  transform: translateY(-3px);
  border-color: #f6c945;
  box-shadow: 0 4px 8px rgba(77, 50, 0, 0.05),
    0 20px 36px -10px rgba(77, 50, 0, 0.18);
}
.role-icon {
  color: #ee4b2b;
  background: rgba(238, 75, 43, 0.1);
  transition: background-color 0.2s ease, color 0.2s ease;
}
.role-card:hover .role-icon {
  color: #ffffff;
  background: #ee4b2b;
}
</style>
